import {Injectable, inject} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {Observable, map} from 'rxjs';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {BidMapBidDto} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidMapBidDto';
import {BidMapClusterDto} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidMapClusterDto';
import {BidMapDetailsDto} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidMapDetailsDto';
import {BidMapFieldDto} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidMapFieldDto';
import {LocalizationString} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/localizationString';
import {BidMapRegionAggregateDto} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidMapRegionAggregateDto';
import {FieldFilter} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/fieldFilter';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {GisCircleMarker} from '@app/pages/electronic-field-map/pages/gis-fields/interfaces/gis-component.interfaces';
import {Marker} from '@components/openstreetmap/interfaces/marker-layer.interface';
import {GisPolygon} from '@components/openstreetmap/interfaces/gis-component.interfaces';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {createRegionCircleMarker} from '@app/utils/circle-marker.util';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {ServerTranslateService} from '@services/server-translate.service';
import {BID_STATUS_USES} from '@shared/data/bid-status-uses-data';
import {
  BID_MAP_CLUSTER_CIRCLE_CLICK_ZOOM,
  BID_MAP_ZOOM_AGGREGATE_MAX,
  BID_MAP_ZOOM_CLUSTER_BAND_MAX,
  BID_MAP_ZOOM_CLUSTER_MIN,
} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-zoom.config';
import {BID_FIELD_POLYGON_FILL, BID_FIELD_POLYGON_STROKE, BID_MAP_ACCENT, BID_MAP_STROKE} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-colors';
import {
  buildGroundSurveyPinDivIconHtml,
  escapeBidMapHtml,
  resolveBidIdFromMapDto,
} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-html.util';
import {
  getBidStatusSeverityTone,
  normalizeBidStatusId,
  pinHexForBidSeverityTone,
} from '@app/pages/crop-monitoring/pages/gis-bids/request/bid-map-status.util';

/** Field row from map details: extras may arrive before OpenAPI stubs are regenerated. */
type BidMapFieldForMapDto = BidMapFieldDto & {
  fieldName?: string | null;
  name?: string | null;
};

@Injectable({providedIn: 'root'})
export class BidMapManagerService {
  private readonly bidService = inject(BidService);
  private readonly regionManager = inject(RegionManagerService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly dateFormatLang = inject(DateFormatLangPipe);

  loadRegionAggregates(filters: FieldFilter[], mapRef: OpenStreetMapComponent | null): Observable<GisCircleMarker[]> {
    return this.bidService
      .cropMonitoringBidGetMapRegionAggregatesPost({query: {filters}})
      .pipe(map(rows => (rows ?? []).map((r, i) => this.aggregateToCircle(r, i, mapRef)).filter((c): c is GisCircleMarker => c != null)));
  }

  loadClusters(
    bounds: {minLat: number; maxLat: number; minLng: number; maxLng: number},
    filters: FieldFilter[],
    mapRef: OpenStreetMapComponent | null,
  ): Observable<{circles: GisCircleMarker[]; pointMarkers: Marker[]}> {
    return this.bidService
      .cropMonitoringBidGetMapClustersPost({
        minLat: bounds.minLat,
        maxLat: bounds.maxLat,
        minLng: bounds.minLng,
        maxLng: bounds.maxLng,
        query: {filters},
      })
      .pipe(
        map(list => {
          const circles: GisCircleMarker[] = [];
          const rows = list ?? [];
          for (let i = 0; i < rows.length; i++) {
            const circle = this.clusterToCircle(rows[i], i, mapRef);
            if (circle) {
              circles.push(circle);
            }
          }
          return {circles, pointMarkers: [] as Marker[]};
        }),
      );
  }

  loadDetails(
    bounds: {minLat: number; maxLat: number; minLng: number; maxLng: number},
    filters: FieldFilter[],
  ): Observable<BidMapDetailsDto> {
    return this.bidService.cropMonitoringBidGetMapDetailsPost({
      minLat: bounds.minLat,
      maxLat: bounds.maxLat,
      minLng: bounds.minLng,
      maxLng: bounds.maxLng,
      query: {filters},
    });
  }

  detailsToLayers(
    details: BidMapDetailsDto | null | undefined,
  ): {polygons: GisPolygon[]; markers: Marker[]} {
    const polygons: GisPolygon[] = [];
    const markers: Marker[] = [];

    const fieldsList = details?.fields ?? [];
    const bidsList = details?.bids ?? [];
    const fieldIdToNameFromBids = this.mapFieldIdToBidFieldNames(bidsList);
    const singlePolyFieldNameFallback =
      fieldsList.length === 1 ? this.uniqueBidFieldNameWhenUnambiguous(bidsList) : null;

    for (const f of fieldsList) {
      const fieldRow = f as BidMapFieldForMapDto;
      const id = fieldRow.fieldId ?? '';
      if (!id || !fieldRow.polygonJson) {
        continue;
      }
      const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(fieldRow.polygonJson);
      if (coordinates.length < 3) {
        continue;
      }
      const count = fieldRow.bidsCount ?? 0;
      const displayName = this.resolveFieldPolygonDisplayName(
        fieldRow,
        id,
        fieldIdToNameFromBids,
        singlePolyFieldNameFallback,
      );
      const fieldIdForNav = String(id).trim();
      polygons.push({
        id: `bid-field-${id}`,
        name: displayName || id,
        type: 'field',
        coordinates,
        strokeColor: BID_FIELD_POLYGON_STROKE,
        color: BID_FIELD_POLYGON_STROKE,
        fillColor: BID_FIELD_POLYGON_FILL,
        fillOpacity: 0.8,
        strokeWidth: 2,
        fill: true,
        data: {fieldId: id, bidsCount: count},
        ...(displayName ? {label: escapeBidMapHtml(displayName), labelColor: '#1a1a1a'} : {}),
        onClick: () => this.openFieldGeneralInNewTab(fieldIdForNav),
      });
    }

    let bidIdx = 0;
    for (const b of bidsList) {
      const lat = b.lat;
      const lng = b.lng;
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
        continue;
      }
      const fill = this.pinFillForBid(b);
      markers.push(
        this.buildBidMarker(`bid-detail-${bidIdx++}`, lat, lng, b, fill, {
          includeAnomalies: true,
        }),
      );
    }

    return {polygons, markers};
  }

  private buildBidMarker(
    id: string,
    lat: number,
    lng: number,
    bid: BidMapBidDto,
    pinFill: string,
    opts: {includeAnomalies?: boolean},
  ): Marker {
    const html = this.buildBidPopupHtml(bid, opts.includeAnomalies ?? false);
    return {
      id,
      lat,
      lng,
      color: pinFill,
      withCluster: false,
      openPopupOnHover: true,
      customIcon: buildGroundSurveyPinDivIconHtml(pinFill),
      customIconSize: [25, 41],
      customIconAnchor: [12, 41],
      customIconPopupAnchor: [1, -34],
      popup: html || undefined,
      onClick: () => this.navigateToBidIfPossible(bid),
      data: bid,
    };
  }

  private navigateToBidIfPossible(bid: BidMapBidDto): void {
    const id = resolveBidIdFromMapDto(bid);
    if (!id) {
      return;
    }
    const urlTree = this.router.createUrlTree(['/crop-monitoring/requests/edit', id]);
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private openFieldGeneralInNewTab(fieldId: string): void {
    const fid = fieldId.trim();
    if (!fid) {
      return;
    }
    const urlTree = this.router.createUrlTree(['/electronic-field-map/fields', fid, 'general']);
    window.open(this.router.serializeUrl(urlTree), '_blank', 'noopener,noreferrer');
  }

  private getBidStateRaw(b: BidMapBidDto): string | number | null | undefined {
    const r = b as Record<string, unknown>;
    if (b.bidState != null && String(b.bidState).trim() !== '') {
      return b.bidState;
    }
    const bidStateId = r['bidStateId'];
    if (bidStateId != null && String(bidStateId).trim() !== '') {
      return bidStateId as string | number;
    }
    const status = r['status'];
    if (status != null && String(status).trim() !== '') {
      return status as string | number;
    }
    return null;
  }

  private pinFillForBid(b: BidMapBidDto): string {
    return pinHexForBidSeverityTone(getBidStatusSeverityTone(normalizeBidStatusId(this.getBidStateRaw(b))));
  }

  private formatWorkPeriodLine(
    startDate: string | null | undefined,
    endDate: string | null | undefined,
  ): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || undefined;
    const start =
      startDate != null && String(startDate).length
        ? this.dateFormatLang.transform(startDate, lang) ?? ''
        : '';
    const end =
      endDate != null && String(endDate).length
        ? this.dateFormatLang.transform(endDate, lang) ?? ''
        : '';
    if (start && end) {
      return `${escapeBidMapHtml(start)} – ${escapeBidMapHtml(end)}`;
    }
    if (start) {
      return escapeBidMapHtml(start);
    }
    if (end) {
      return `– ${escapeBidMapHtml(end)}`;
    }
    return escapeBidMapHtml(this.translate.instant('GIS_BIDS_MAP_PERIOD_EMPTY'));
  }

  private formatBidCropNames(crops: BidMapBidDto['cropNames'] | unknown[] | null | undefined): string {
    if (crops == null) {
      return '';
    }
    if (Array.isArray(crops)) {
      const parts: string[] = [];
      for (const c of crops) {
        const t = this.serverTranslate.translate(c, 'names');
        if (!t) {
          continue;
        }
        if (Array.isArray(t)) {
          for (const s of t) {
            if (s && String(s).trim()) {
              parts.push(String(s).trim());
            }
          }
        } else if (String(t).trim()) {
          parts.push(String(t).trim());
        }
      }
      return [...new Set(parts)].slice().sort().join(', ');
    }
    const one = this.serverTranslate.translate({names: crops as LocalizationString}, 'names');
    if (Array.isArray(one)) {
      return one.filter(s => s && String(s).trim()).join(', ');
    }
    return one?.trim() ?? '';
  }

  private buildBidPopupHtml(bid: BidMapBidDto, includeAnomalies: boolean): string {
    const statusId = normalizeBidStatusId(this.getBidStateRaw(bid));
    const meta = statusId != null ? BID_STATUS_USES.find(s => s.id === statusId) : undefined;
    const labelRaw = meta ? (this.serverTranslate.translate(meta, 'names') ?? '') : '';
    const label = Array.isArray(labelRaw) ? labelRaw.join(', ') : labelRaw;
    const tone = getBidStatusSeverityTone(statusId);
    const periodInner = this.formatWorkPeriodLine(bid.startDate, bid.endDate);
    const workPeriodCaption = escapeBidMapHtml(this.translate.instant('Work Period'));
    const anomaliesLabel = escapeBidMapHtml(this.translate.instant('Number of anomalies'));
    const anomaliesCount = bid.anomalieCount ?? 0;
    const badge = label
      ? `<span class="bid-map-popup__badge bid-map-popup__badge--${tone}">${escapeBidMapHtml(label)}</span>`
      : '';
    const periodRow = `<div class="bid-map-popup__row"><span class="bid-map-popup__row-label">${workPeriodCaption}</span><span class="bid-map-popup__row-value bid-map-popup__period">${periodInner}</span></div>`;
    const fieldCaption = escapeBidMapHtml(this.translate.instant('FIELD'));
    const fieldNameLine = bid.fieldName?.trim() ? bid.fieldName.trim() : '';
    const fieldValue = fieldNameLine ? escapeBidMapHtml(fieldNameLine) : escapeBidMapHtml('–');
    const fieldRow = `<div class="bid-map-popup__row"><span class="bid-map-popup__row-label">${fieldCaption}</span><span class="bid-map-popup__row-value">${fieldValue}</span></div>`;
    const cropCaption = escapeBidMapHtml(this.translate.instant('Crop'));
    const cropLine = this.formatBidCropNames(bid.cropNames);
    const cropValue = cropLine ? escapeBidMapHtml(cropLine) : escapeBidMapHtml('–');
    const cropRow = `<div class="bid-map-popup__row"><span class="bid-map-popup__row-label">${cropCaption}</span><span class="bid-map-popup__row-value">${cropValue}</span></div>`;
    const foremanCaption = escapeBidMapHtml(this.translate.instant('Foreman'));
    const workerCaption = escapeBidMapHtml(this.translate.instant('Field worker'));
    const foremanLine = bid.foreman?.trim() ? bid.foreman.trim() : '';
    const workerLine = bid.worker?.trim() ? bid.worker.trim() : '';
    const foremanValue = foremanLine ? escapeBidMapHtml(foremanLine) : escapeBidMapHtml('–');
    const workerValue = workerLine ? escapeBidMapHtml(workerLine) : escapeBidMapHtml('–');
    const foremanRow = `<div class="bid-map-popup__row"><span class="bid-map-popup__row-label">${foremanCaption}</span><span class="bid-map-popup__row-value">${foremanValue}</span></div>`;
    const workerRow = `<div class="bid-map-popup__row"><span class="bid-map-popup__row-label">${workerCaption}</span><span class="bid-map-popup__row-value">${workerValue}</span></div>`;
    const anomaliesRow = includeAnomalies
      ? `<div class="bid-map-popup__row bid-map-popup__row--anomalies"><span class="bid-map-popup__row-label">${anomaliesLabel}</span><span class="bid-map-popup__row-value">${anomaliesCount}</span></div>`
      : '';
    return (
      `<div class="bid-map-popup bid-map-popup--bid">` +
      `${badge}${cropRow}${foremanRow}${workerRow}${fieldRow}${anomaliesRow}${periodRow}</div>`
    );
  }

  /** Bids sometimes carry `fieldName` plus `fieldId` (stub may omit `fieldId`). */
  private mapFieldIdToBidFieldNames(bids: BidMapBidDto[]): Map<string, string> {
    const m = new Map<string, string>();
    for (const b of bids ?? []) {
      const fidRaw = (b as Record<string, unknown>)['fieldId'];
      const fieldId = fidRaw != null ? String(fidRaw).trim() : '';
      const fn = b.fieldName?.trim();
      if (fieldId && fn && !m.has(fieldId)) {
        m.set(fieldId, fn);
      }
    }
    return m;
  }

  /**
   * When the viewport has exactly one field polygon but bids only expose `fieldName`, reuse that unique name.
   */
  private uniqueBidFieldNameWhenUnambiguous(bids: BidMapBidDto[]): string | null {
    const raw = bids.map(b => b.fieldName?.trim()).filter((n): n is string => !!n && n.length > 0);
    const uniq = [...new Set(raw)];
    return uniq.length === 1 ? uniq[0]! : null;
  }

  private resolveFieldPolygonDisplayName(
    fieldRow: BidMapFieldForMapDto,
    fieldId: string,
    nameByFieldIdFromBids: Map<string, string>,
    singleFieldBidNameFallback: string | null,
  ): string {
    const row = fieldRow as Record<string, unknown>;
    const strip = (v: unknown): string =>
      typeof v === 'string' && v.trim() ? v.trim() : '';

    const fromPayload =
      strip(fieldRow.fieldName) ||
      strip(fieldRow.name) ||
      strip(row['title']) ||
      strip(row['label']);

    if (fromPayload) {
      return fromPayload;
    }

    const fromBid = nameByFieldIdFromBids.get(fieldId)?.trim();
    if (fromBid) {
      return fromBid;
    }

    if (singleFieldBidNameFallback) {
      return singleFieldBidNameFallback;
    }

    return '';
  }

  private aggregateToCircle(
    a: BidMapRegionAggregateDto,
    index: number,
    mapRef: OpenStreetMapComponent | null,
  ): GisCircleMarker | null {
    const lat = a.regionCenter?.lat;
    const lng = a.regionCenter?.lng;
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    const regionId = a.regionId != null && String(a.regionId).length ? String(a.regionId) : `idx-${index}`;
    const count = a.bidsCount ?? 0;
    if (count < 1) {
      return null;
    }
    const name = this.pickRegionName(a.regionNames);
    const cache = this.regionManager.getRegionPolygonsCache().get(regionId);

    if (cache?.polygon && count > 0) {
      const poly = cache.polygon;
      const marker = createRegionCircleMarker(regionId, count, cache.center, poly, BID_MAP_ACCENT, mapRef ?? undefined);
      marker.fillColor = BID_MAP_ACCENT;
      marker.strokeColor = BID_MAP_STROKE;
      marker.minZoom = 0;
      marker.maxZoom = BID_MAP_ZOOM_AGGREGATE_MAX;
      marker.showByZoomLevel = true;
      if (name && marker.data) {
        marker.data = {
          regionId: marker.data.regionId,
          regionName: name,
          fieldCount: marker.data.fieldCount,
          level: marker.data.level,
        };
      }
      marker.onClick = () => {
        const lc = mapRef?.getLayersController();
        if (!lc) {
          return;
        }
        lc.centerToPoint(marker.center[0], marker.center[1], BID_MAP_ZOOM_CLUSTER_MIN, true);
      };
      return marker;
    }

    const tooltipName = name || regionId;
    return {
      id: `bid-region-${regionId}`,
      center: [lat, lng],
      radius: 30,
      count,
      fillColor: BID_MAP_ACCENT,
      strokeColor: BID_MAP_STROKE,
      fillOpacity: 0.6,
      strokeWidth: 2,
      type: 'bid-region-count',
      minZoom: 0,
      maxZoom: BID_MAP_ZOOM_AGGREGATE_MAX,
      showByZoomLevel: true,
      data: {
        regionId,
        regionName: tooltipName,
        fieldCount: count,
      },
      onClick: circle => {
        const lc = mapRef?.getLayersController();
        if (!lc) {
          return;
        }
        lc.centerToPoint(circle.center[0], circle.center[1], BID_MAP_ZOOM_CLUSTER_MIN, true);
      },
    };
  }

  private clusterToCircle(
    c: BidMapClusterDto,
    index: number,
    mapRef: OpenStreetMapComponent | null,
  ): GisCircleMarker | null {
    const lat = c.lat;
    const lng = c.lng;
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    const count = c.count ?? 0;
    if (count < 1) {
      return null;
    }
    return {
      id: `bid-cluster-${index}-${lat}-${lng}`,
      center: [lat, lng],
      count: count || 0,
      fillColor: BID_MAP_ACCENT,
      strokeColor: BID_MAP_STROKE,
      fillOpacity: 0.65,
      showByZoomLevel: true,
      minZoom: BID_MAP_ZOOM_CLUSTER_MIN,
      maxZoom: BID_MAP_ZOOM_CLUSTER_BAND_MAX,
      type: 'bid-cluster',
      data: {regionId: 'bid-cluster', regionName: '', fieldCount: count || 0},
      onClick: () => {
        const lc = mapRef?.getLayersController();
        lc?.centerToPoint(lat, lng, BID_MAP_CLUSTER_CIRCLE_CLICK_ZOOM, true);
      },
    };
  }

  private pickRegionName(names: BidMapRegionAggregateDto['regionNames']): string {
    if (!names || typeof names !== 'object') {
      return '';
    }
    const lang = this.translate.currentLang || 'en';
    const short = lang.split('-')[0] ?? 'en';
    return (
      names[lang] ||
      names[short] ||
      names['en'] ||
      names['ru'] ||
      Object.values(names).find(v => v && String(v).trim()) ||
      ''
    );
  }
}
